import React, { useState, useEffect } from 'react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FolderOpen, 
  Plus, 
  Edit, 
  Trash2, 
  Copy,
  Play,
  Search,
  ListChecks,
  Clock,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';

const PRACTICE_AREAS = [
  'Personal Injury', 'Family Law', 'Corporate', 'Criminal Defense', 
  'Real Estate', 'Estate Planning', 'Immigration', 'Employment', 'Bankruptcy', 'Other'
];

const BILLING_TYPES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'flat', label: 'Flat Fee' },
  { value: 'contingency', label: 'Contingency' },
];

export const CaseTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [practiceFilter, setPracticeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [applyDialog, setApplyDialog] = useState(null);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    practice_area: '',
    description: '',
    estimated_hours: '',
    default_billing_type: 'hourly',
    default_rate: '',
    default_tasks: [],
    required_documents: [],
  });

  const [newTask, setNewTask] = useState({ title: '', relative_due_days: 0, priority: 'medium' });
  const [newDoc, setNewDoc] = useState({ name: '', required: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, casesRes] = await Promise.all([
        api.get('/case-templates'),
        api.get('/cases'),
      ]);
      setTemplates(templatesRes.data);
      setCases(casesRes.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await api.get('/case-templates/defaults/seed');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to create default templates');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        default_rate: formData.default_rate ? parseFloat(formData.default_rate) : null,
      };
      
      if (editingTemplate) {
        await api.put(`/case-templates/${editingTemplate.template_id}`, data);
        toast.success('Template updated');
      } else {
        await api.post('/case-templates', data);
        toast.success('Template created');
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleApplyTemplate = async () => {
    if (!applyDialog || !selectedCase) return;
    try {
      const response = await api.post(`/case-templates/${applyDialog}/apply`, { case_id: selectedCase });
      toast.success(`Template applied! ${response.data.tasks_created} tasks created.`);
      setApplyDialog(null);
      setSelectedCase('');
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/case-templates/${deleteId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    } finally {
      setDeleteId(null);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      practice_area: template.practice_area,
      description: template.description || '',
      estimated_hours: template.estimated_hours?.toString() || '',
      default_billing_type: template.default_billing_type || 'hourly',
      default_rate: template.default_rate?.toString() || '',
      default_tasks: template.default_tasks || [],
      required_documents: template.required_documents || [],
    });
    setIsDialogOpen(true);
  };

  const addTask = () => {
    if (!newTask.title) return;
    setFormData({
      ...formData,
      default_tasks: [...formData.default_tasks, { ...newTask }],
    });
    setNewTask({ title: '', relative_due_days: 0, priority: 'medium' });
  };

  const removeTask = (index) => {
    setFormData({
      ...formData,
      default_tasks: formData.default_tasks.filter((_, i) => i !== index),
    });
  };

  const addDocument = () => {
    if (!newDoc.name) return;
    setFormData({
      ...formData,
      required_documents: [...formData.required_documents, { ...newDoc }],
    });
    setNewDoc({ name: '', required: false });
  };

  const removeDocument = (index) => {
    setFormData({
      ...formData,
      required_documents: formData.required_documents.filter((_, i) => i !== index),
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      practice_area: '',
      description: '',
      estimated_hours: '',
      default_billing_type: 'hourly',
      default_rate: '',
      default_tasks: [],
      required_documents: [],
    });
    setEditingTemplate(null);
    setIsDialogOpen(false);
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPractice = practiceFilter === 'all' || t.practice_area === practiceFilter;
    return matchesSearch && matchesPractice;
  });

  if (loading) {
    return <PageLoader text="Loading case templates..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="case-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Matter Templates</h1>
          <p className="text-slate-500 mt-1">Create reusable workflows for common case types</p>
        </div>
        <div className="flex items-center gap-2">
          {templates.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Add Defaults
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-template-btn">
                <Plus className="h-4 w-4" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Case Template'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Template Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Personal Injury - MVA"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="practice_area">Practice Area *</Label>
                    <Select value={formData.practice_area} onValueChange={(v) => setFormData({ ...formData, practice_area: v })}>
                      <SelectTrigger><SelectValue placeholder="Select area" /></SelectTrigger>
                      <SelectContent>
                        {PRACTICE_AREAS.map(area => (
                          <SelectItem key={area} value={area}>{area}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="estimated_hours">Est. Hours</Label>
                    <Input
                      id="estimated_hours"
                      type="number"
                      value={formData.estimated_hours}
                      onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="default_billing_type">Billing Type</Label>
                    <Select value={formData.default_billing_type} onValueChange={(v) => setFormData({ ...formData, default_billing_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BILLING_TYPES.map(b => (
                          <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="default_rate">Default Rate</Label>
                    <Input
                      id="default_rate"
                      type="number"
                      value={formData.default_rate}
                      onChange={(e) => setFormData({ ...formData, default_rate: e.target.value })}
                      placeholder="$0.00"
                    />
                  </div>
                </div>

                {/* Tasks Section */}
                <div className="space-y-2">
                  <Label>Default Tasks</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Task title"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Days"
                      value={newTask.relative_due_days}
                      onChange={(e) => setNewTask({ ...newTask, relative_due_days: parseInt(e.target.value) || 0 })}
                      className="w-20"
                    />
                    <Button type="button" variant="outline" onClick={addTask}>Add</Button>
                  </div>
                  {formData.default_tasks.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.default_tasks.map((task, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 p-2 rounded text-sm">
                          <span>{task.title} <span className="text-slate-500">(+{task.relative_due_days} days)</span></span>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeTask(i)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Documents Section */}
                <div className="space-y-2">
                  <Label>Required Documents</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Document name"
                      value={newDoc.name}
                      onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={addDocument}>Add</Button>
                  </div>
                  {formData.required_documents.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.required_documents.map((doc, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 p-2 rounded text-sm">
                          <span>{doc.name}</span>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeDocument(i)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit">{editingTemplate ? 'Update' : 'Create'} Template</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={practiceFilter} onValueChange={setPracticeFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Practice Area" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Practice Areas</SelectItem>
            {PRACTICE_AREAS.map(area => (
              <SelectItem key={area} value={area}>{area}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.template_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <Badge variant="outline" className="mt-2">{template.practice_area}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setApplyDialog(template.template_id)} className="h-8 w-8 p-0 text-green-600">
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(template)} className="h-8 w-8 p-0">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(template.template_id)} className="h-8 w-8 p-0 text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {template.description && (
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">{template.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <ListChecks className="h-3 w-3" />
                    {template.default_tasks?.length || 0} tasks
                  </span>
                  {template.estimated_hours && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {template.estimated_hours}h
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FolderOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No templates found</h3>
            <p className="text-slate-500 mb-4">
              Create templates to streamline your case workflows
            </p>
            {templates.length === 0 && (
              <Button onClick={handleSeedDefaults} className="gap-2">
                <Sparkles className="h-4 w-4" />
                Add Default Templates
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Apply Template Dialog */}
      <AlertDialog open={!!applyDialog} onOpenChange={() => { setApplyDialog(null); setSelectedCase(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply Template to Case</AlertDialogTitle>
            <AlertDialogDescription>
              Select a case to apply this template. This will create tasks and a document checklist.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedCase} onValueChange={setSelectedCase}>
              <SelectTrigger>
                <SelectValue placeholder="Select a case" />
              </SelectTrigger>
              <SelectContent>
                {cases.map(c => (
                  <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApplyTemplate} disabled={!selectedCase}>
              Apply Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CaseTemplates;
